import * as React from "react"
import { Box, Typography, keyframes, Table, TableHead, TableBody, TableRow, TableCell, Chip } from "@mui/material"

const cursorBlink = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0; }
`


function parseInline(line) {
	line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
	line = line.replace(/\*(.*?)\*/g, '<em>$1</em>')
	line = line.replace(/`(.*?)`/g, '<code>$1</code>')
	return line
}

function isTableSeparator(line) {
	return /^\|?[\s:-]+\|[\s|:-]+$/.test(line)
}

function parseTableRow(line) {
	return line.split('|').map(c => c.trim()).filter(Boolean)
}

const statusBadges = {
	beat: { label: 'Beat', bgcolor: '#ECFDF3', color: '#027A48' },
	meet: { label: 'Meet', bgcolor: '#EFF8FF', color: '#175CD3' },
	lose: { label: 'Lose', bgcolor: '#FEF3F2', color: '#B42318' },
}

function isNumeric(str) {
	const cleaned = str.replace(/<[^>]+>/g, '').trim()
	return /^[\d€$£¥%,.\-+\s]+$/.test(cleaned) && /\d/.test(cleaned)
}

function renderCellContent(cell) {
	const stripped = cell.replace(/<[^>]+>/g, '').trim().toLowerCase()
	const badge = statusBadges[stripped]
	if (badge) {
		return <Chip label={badge.label} size="small" sx={{
			bgcolor: badge.bgcolor, color: badge.color,
			fontWeight: 600, fontSize: '0.75rem', height: 24,
		}} />
	}
	return <span dangerouslySetInnerHTML={{ __html: parseInline(cell) }} />
}

function MarkdownTable({ rows }) {
	const header = rows[0]
	const body = rows.slice(1)

	return (
		<Box sx={{ overflowX: 'auto', my: 2 }}>
			<Table size="small" sx={{
				'& .MuiTableCell-root': {
					fontSize: '0.8rem',
					py: 1,
					px: 1.5,
					whiteSpace: 'nowrap',
					borderColor: '#EAECF0',
				},
			}}>
				<TableHead>
					<TableRow sx={{ bgcolor: '#F9FAFB' }}>
						{header.map((cell, i) => (
							<TableCell key={i} sx={{ fontWeight: 600, color: '#475467' }}
								dangerouslySetInnerHTML={{ __html: parseInline(cell) }} />
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{body.map((row, i) => (
						<TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#FAFAFA' } }}>
							{row.map((cell, j) => {
								const parsed = parseInline(cell)
								return <TableCell key={j} sx={{
									textAlign: isNumeric(parsed) ? 'right' : 'left',
								}}>
									{renderCellContent(cell)}
								</TableCell>
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	)
}


function parseBlocks(text) {
	const lines = text.split('\n')
	const blocks = []
	let i = 0

	while (i < lines.length) {
		const trimmed = lines[i].trim()

		// Empty line
		if (!trimmed) {
			blocks.push({ type: 'spacer' })
			i++
			continue
		}

		// Table: detect header + separator + rows
		if (trimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]?.trim())) {
			const tableRows = [parseTableRow(trimmed)]
			i += 2 // skip header + separator
			while (i < lines.length && lines[i].trim().includes('|') && !isTableSeparator(lines[i].trim())) {
				tableRows.push(parseTableRow(lines[i].trim()))
				i++
			}
			blocks.push({ type: 'table', rows: tableRows })
			continue
		}

		// Headers
		if (trimmed.startsWith('### ')) { blocks.push({ type: 'h3', text: trimmed.slice(4) }); i++; continue }
		if (trimmed.startsWith('## ')) { blocks.push({ type: 'h2', text: trimmed.slice(3) }); i++; continue }
		if (trimmed.startsWith('# ')) { blocks.push({ type: 'h1', text: trimmed.slice(2) }); i++; continue }

		// Horizontal rule
		if (trimmed === '---' || trimmed === '***') { blocks.push({ type: 'hr' }); i++; continue }

		// Bullet list
		if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
			const items = []
			while (i < lines.length && (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))) {
				items.push(lines[i].trim().slice(2))
				i++
			}
			blocks.push({ type: 'ul', items })
			continue
		}

		// Numbered list
		if (/^\d+\.\s/.test(trimmed)) {
			const items = []
			while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
				items.push(lines[i].trim().replace(/^\d+\.\s/, ''))
				i++
			}
			blocks.push({ type: 'ol', items })
			continue
		}

		// Paragraph
		blocks.push({ type: 'p', text: trimmed })
		i++
	}

	return blocks
}


const headerVariants = {
	h1: { component: 'h1', variant: 'h6', sx: { mt: 2.5, mb: 1, fontWeight: 700, color: '#7F56D9' } },
	h2: { component: 'h2', variant: 'subtitle1', sx: { mt: 2.5, mb: 1, fontWeight: 600, color: '#7F56D9' } },
	h3: { component: 'h3', variant: 'subtitle2', sx: { mt: 2, mb: 0.75, fontWeight: 600, color: '#7F56D9' } },
}

const highlightKeywords = [
	'most important finding',
	'key takeaway',
	'conclusion',
	'strategic recommendation',
	'bottom line',
]

function isHighlightHeader(text) {
	const lower = text.toLowerCase()
	return highlightKeywords.some(kw => lower.includes(kw))
}


export default function AiInsightsContent({ text, isStreaming }) {
	const containerRef = React.useRef(null)

	React.useEffect(() => {
		if (isStreaming && containerRef.current)
			containerRef.current.scrollTop = containerRef.current.scrollHeight
	}, [text, isStreaming])

	const blocks = React.useMemo(() => parseBlocks(text), [text])

	function renderBlock(block, i) {
		if (block.type === 'spacer')
			return <Box key={i} sx={{ height: '0.75em' }} />
		if (block.type === 'hr')
			return <Box key={i} component="hr" sx={{ my: 2.5, border: 'none', borderTop: '1px solid #EAECF0' }} />
		if (block.type === 'table')
			return <MarkdownTable key={i} rows={block.rows} />
		if (headerVariants[block.type]) {
			const cfg = headerVariants[block.type]
			return <Typography key={i} component={cfg.component} variant={cfg.variant} sx={cfg.sx}
				dangerouslySetInnerHTML={{ __html: parseInline(block.text) }} />
		}
		if (block.type === 'ul')
			return <Box key={i} component="ul" sx={{ pl: 2.5, my: 0.75 }}>
				{block.items.map((item, j) => (
					<Box key={j} component="li" sx={{ mb: 0.75, '&::marker': { color: '#98A2B3' } }}
						dangerouslySetInnerHTML={{ __html: parseInline(item) }} />
				))}
			</Box>
		if (block.type === 'ol')
			return <Box key={i} component="ol" sx={{ pl: 2.5, my: 0.75 }}>
				{block.items.map((item, j) => (
					<Box key={j} component="li" sx={{ mb: 1, pl: 0.5,
						'&::marker': { color: '#7F56D9', fontWeight: 600 } }}
						dangerouslySetInnerHTML={{ __html: parseInline(item) }} />
				))}
			</Box>
		return <Typography key={i} variant="body2" sx={{ mb: 0.75, lineHeight: 1.7, color: '#475467' }}
			dangerouslySetInnerHTML={{ __html: parseInline(block.text) }} />
	}

	// Detect highlight sections (key takeaway / most important finding)
	const highlightRanges = React.useMemo(() => {
		const ranges = new Set()
		let inHighlight = false
		for (let i = 0; i < blocks.length; i++) {
			const b = blocks[i]
			if ((b.type === 'h1' || b.type === 'h2' || b.type === 'h3') && isHighlightHeader(b.text)) {
				inHighlight = true
				continue // skip the header itself from the highlight box content
			}
			if (inHighlight) {
				if ((b.type === 'h1' || b.type === 'h2' || b.type === 'h3') || b.type === 'hr') {
					inHighlight = false
				} else {
					ranges.add(i)
				}
			}
		}
		return ranges
	}, [blocks])

	return (
		<Box
			ref={containerRef}
			sx={{
				fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
				fontSize: '0.875rem',
				lineHeight: 1.7,
				color: '#475467',
				'& code': {
					bgcolor: '#F2F4F7',
					px: 0.5,
					borderRadius: 0.5,
					fontFamily: 'monospace',
					fontSize: '0.8rem',
				},
			}}
		>
			{(() => {
				const elements = []
				let i = 0

				while (i < blocks.length) {
					const block = blocks[i]
					const isHighlightHeader_ = (block.type === 'h1' || block.type === 'h2' || block.type === 'h3') && isHighlightHeader(block.text)

					// If this is a highlight header, render it + collect content into a highlight box
					if (isHighlightHeader_) {
						elements.push(
							<Typography key={i} component="h2" variant="subtitle1"
								sx={{ mt: 2.5, mb: 1, fontWeight: 700, color: '#7F56D9' }}
								dangerouslySetInnerHTML={{ __html: parseInline(block.text) }} />
						)
						i++
						// Collect following content blocks into highlight box
						const children = []
						while (i < blocks.length && highlightRanges.has(i)) {
							children.push(renderBlock(blocks[i], i))
							i++
						}
						if (children.length > 0) {
							elements.push(
								<Box key={`hl-${i}`} sx={{
									bgcolor: '#F9F5FF',
									borderLeft: '4px solid #7F56D9',
									borderRadius: '0 8px 8px 0',
									px: 2.5, py: 2, my: 1,
								}}>
									{children}
								</Box>
							)
						}
						continue
					}

					elements.push(renderBlock(block, i))
					i++
				}
				return elements
			})()}

			{isStreaming && (
				<Box
					component="span"
					sx={{
						display: 'inline-block',
						width: 3,
						height: '1.1em',
						ml: 0.5,
						bgcolor: '#7F56D9',
						borderRadius: 0.5,
						verticalAlign: 'text-bottom',
						animation: `${cursorBlink} 1s steps(1) infinite`,
					}}
				/>
			)}
		</Box>
	)
}
